/**
 * Number theory implementation grounded in the Ontology Genesis Charter.
 *
 * All numbers (integers and rationals) are expressed as [Link] topology,
 * with arithmetic defined as Term Rewriting System (TRS) on slot pointers.
 *
 * ## Structural Genera
 *
 * | Genus     | Topology              | parent | target |
 * |-----------|-----------------------|--------|--------|
 * | Zero      | Link(root, root)      | root   | root   |
 * | Positive  | Link(N_{n-1}, root)   | active | root   |
 * | Negative  | Link(root, N_{-(n-1)})| root   | active |
 * | Rational  | Link(N_p, N_q)        | active | active |
 *
 * ## Key Invariants
 * - Core Link operators (add, mul, div) are pure un-normalized TRS (Rational Fractional Manifold).
 * - Equivalent rationals are topologically identified: Link(N1, D1) ~ Link(N2, D2) <=> N1 * D2 == N2 * D1.
 * - Normalization collapses denominator-1 rationals back to 1D integers and ensures positive denominators.
 * - Quantity class caches the parsed properties and serves as the primary number projection.
 */

package com.oasisfeng.invaluable

import kotlin.math.abs

// ============================================================
//  Number Construction
// ============================================================

/** N_0 ≡ Link(root, root) — ground state: additive identity and multiplicative absorber. */
val N0: Link = distinct()
val N1: NestedLink = N0.then(null)

private val integerCache = mutableListOf(N0, N1)

/**
 * Construct the Link representation of integer [value].
 *
 * - Positive n: parent-chain Link(N_{n-1}, root) — pure substance accumulation
 * - Negative n: target-chain Link(root, N_{-(n-1)}) — pure constraint accumulation
 * - Zero: Link(root, root) — symmetric ground state
 */
internal fun integerLink(value: Int): Link {
    if (value == 0) return N0
    if (value > 0) {
        if (value < integerCache.size) return integerCache[value]
        var current = integerCache.last()
        while (value >= integerCache.size) {
            current = current.then(null)
            integerCache.add(current)
        }
        return integerCache[value]
    } else {
        var current: Link = N0
        repeat(-value) { current = (null as Link?).then(current) }
        return current
    }
}

/**
 * Return the integer value if this Link is a valid 1D integer (parent-chain for positive, target-chain for negative),
 * otherwise return null.
 */
fun Link.toIntOrNull(): Int? {
    var current = this
    var value = 0
    while (true) {
        val p = current.parentOrNull()
        @OptIn(InternalTopologyApi::class) val t = current.rawTargetOrNull()
        if (p == null && t == null) return value
        if (p != null && t == null) {
            if (value < 0) return null
            value++
            current = p
        } else if (p == null && t != null) {
            if (value > 0) return null
            value--
            current = t
        } else {
            return null
        }
    }
}

/** Return a [Quantity] if this Link is a valid 1D integer or 2D rational topology, otherwise return null. */
fun Link.toQuantityOrNull(): Quantity? {
    val p = parentOrNull()
    @OptIn(InternalTopologyApi::class) val t = rawTargetOrNull()

    if (p == null && t == null) return Quantity.ZERO

    if (p != null && t != null) {
        val num = p.toIntOrNull() ?: return null
        val den = t.toIntOrNull() ?: return null
        if (den == 0) return null
        return Quantity(num, den, this)
    } else return Quantity(toIntOrNull() ?: return null, 1, this)
}

// ============================================================
//  Integer Value Extraction (for debugging and normalization)
// ============================================================

/**
 * Walk the integer chain and return the numeric value.
 *
 * Iteratively counts parent-chain depth (positive) or target-chain depth (negative).
 * @throws IllegalStateException if this Link is a rational (2D structure).
 */
internal fun Link.toIntValue(): Int {
    var current = this
    var value = 0
    while (true) {
        val p = current.parentOrNull()
        @OptIn(InternalTopologyApi::class) val t = current.rawTargetOrNull()
        if (p == null && t == null) return value
        if (p != null && t == null) { value++; current = p }
        else if (p == null && t != null) { value--; current = t }
        else error("Cannot extract integer value from rational: $this")
    }
}

/**
 * Human-readable display of a number Link (integer or rational).
 * For debugging and test assertion messages only — not a stable encoding.
 */
internal fun Link.toNumberString(): String {
    val q = toQuantityOrNull() ?: return "invalid"
    return q.toString()
}

// ============================================================
//  Chiral Inversion I (Charter §1.4)
// ============================================================

/**
 * Recursive parent↔target swap.
 *
 *     I(root) ≡ root
 *     I(Link(P, T)) ≡ Link(I(T), I(P))
 *
 * Emergent algebraic semantics depend on the operand's structural dimensionality:
 * - On 1D integers: negation (additive inverse) — swaps the active axis
 * - On 2D rationals: reciprocal (multiplicative inverse) — swaps numerator/denominator
 *
 * This single geometric operation manifests as different algebraic inverses depending
 * on dimension — a structural consequence, not a design choice.
 */
internal fun Link.chiralInverse(): Link {
    val p = parentOrNull()
    @OptIn(InternalTopologyApi::class) val t = rawTargetOrNull()
    // I(Link(P, T)) = Link(I(T), I(P))
    // null represents root; I(root) = root = null
    val invertedP: Link? = p?.chiralInverse()
    val invertedT: Link? = t?.chiralInverse()
    return invertedT.then(invertedP)
}

// ============================================================
//  Addition ⊕ (Pure TRS on Rational Fractional Manifold)
// ============================================================

/**
 * TRS addition extended with rational rules.
 *
 * Integer rules (applied by decreasing specificity):
 * - Identity: X ⊕ N_0 ≡ X, N_0 ⊕ X ≡ X
 * - Annihilation: Link(P, root) ⊕ Link(root, T) ≡ P ⊕ T
 * - Positive propagation: X ⊕ Link(P, root) ≡ Link(X ⊕ P, root)
 * - Negative propagation: X ⊕ Link(root, T) ≡ Link(root, X ⊕ T)
 *
 * Rational extension: dispatch to cross-multiplication when either operand is 2D.
 */
@OptIn(InternalTopologyApi::class)
internal infix fun Link.add(other: Link): Link {
    val lP = this.parentOrNull()
    val lT = this.rawTargetOrNull()
    val rP = other.parentOrNull()
    val rT = other.rawTargetOrNull()

    val lIsRational = lP != null && lT != null
    val rIsRational = rP != null && rT != null

    if (lIsRational || rIsRational) {
        val a = if (lIsRational) lP else this
        val b = if (lIsRational) lT else integerLink(1)
        val c = if (rIsRational) rP else other
        val d = if (rIsRational) rT else integerLink(1)
        // (a/b) + (c/d) = (a*d + b*c) / (b*d)
        return ((a mul d) add (b mul c)).then(b mul d)
    }

    return addIntegers(this, lP, lT, other, rP, rT)
}

/** Integer-only addition TRS (Charter §2.1). */
private fun addIntegers(left: Link, lP: Link?, lT: Link?, right: Link, rP: Link?, rT: Link?): Link {
    val lfZero = lP == null && lT == null
    val rfZero = rP == null && rT == null

    // Identity (highest priority — guards against N_0's dual-pattern ambiguity)
    if (lfZero) return right
    if (rfZero) return left

    val lfPos = lP != null && lT == null
    val lfNeg = lP == null && lT != null
    val rfPos = rP != null && rT == null
    val rfNeg = rP == null && rT != null

    // Annihilation (constrains both operands — higher specificity than 2.1.3/2.1.4)
    if (lfPos && rfNeg) return lP add rT
    if (lfNeg && rfPos) return lT add rP

    // Positive propagation
    if (rfPos) return (left add rP).then(null as Link?)

    // Negative propagation
    if (rfNeg) return (null as Link?).then(left add rT)

    error("Unreachable: all integer genera covered")
}

// ============================================================
//  Multiplication ⊗ (Pure TRS on Rational Fractional Manifold)
// ============================================================

@OptIn(InternalTopologyApi::class)
internal infix fun Link.mul(other: Link): Link {
    val lP = this.parentOrNull()
    val lT = this.rawTargetOrNull()
    val rP = other.parentOrNull()
    val rT = other.rawTargetOrNull()

    val lIsRational = lP != null && lT != null
    val rIsRational = rP != null && rT != null

    if (lIsRational || rIsRational) {
        val a = if (lIsRational) lP else this
        val b = if (lIsRational) lT else integerLink(1)
        val c = if (rIsRational) rP else other
        val d = if (rIsRational) rT else integerLink(1)
        // (a/b) * (c/d) = (a*c)/(b*d)
        return (a mul c).then(b mul d)
    }

    // --- Integer paths (1D operands, original TRS rules) ---

    val lfZero = lP == null && lT == null
    val rfZero = rP == null && rT == null

    // Optimization: left-zero (derivable from rules 2.2.1+2.2.2 by induction, but saves O(n) recursion)
    if (lfZero) return N0

    // Zero collapse
    if (rfZero) return N0

    val rfPos = rP != null && rT == null
    val rfNeg = rP == null && rT != null

    // Positive step fractal modulation
    if (rfPos) return (this mul rP) add this

    // Negative step fractal modulation
    if (rfNeg) return (this mul rT) add this.chiralInverse()

    error("Unreachable: all integer genera covered")
}

// ============================================================
//  Division ⊘ (Pure TRS on Rational Fractional Manifold)
// ============================================================

/**
 * Division: the act of placing dividend in the parent slot and divisor in the target slot,
 * then normalizing to canonical form.
 *
 * - X ⊘ N_1 ≡ X (unit division)
 * - X ⊘ X ≡ N_1 (self-division, X ≠ 0)
 * - N_0 ⊘ X ≡ N_0 (zero dividend)
 * - General: (a/b) ÷ (c/d) = (a*d) / (b*c)
 *
 * When the result is an exact integer (denominator reduces to 1), the rational
 * collapses back to the 1D integer chain via normalization rule N.3.
 */
@OptIn(InternalTopologyApi::class)
internal infix fun Link.div(other: Link): Link {
    val lP = this.parentOrNull(); val lT = this.rawTargetOrNull()
    val rP = other.parentOrNull(); val rT = other.rawTargetOrNull()
    require(!(rP == null && rT == null)) { "Division by zero" }
    if (lP == null && lT == null) return N0

    val lIsRational = lP != null && lT != null
    val rIsRational = rP != null && rT != null

    val a = if (lIsRational) lP else this
    val b = if (lIsRational) lT else integerLink(1)
    val c = if (rIsRational) rP else other
    val d = if (rIsRational) rT else integerLink(1)

    // (a/b) / (c/d) = (a*d) / (b*c)
    return (a mul d).then(b mul c)
}

// ============================================================
//  Quotient Folding (Topological Reduction)
// ============================================================

/**
 * Actively fold/reduce a number Link into its canonical form.
 */
@OptIn(InternalTopologyApi::class)
internal fun Link.normalize(): Link {
    if (parentOrNull() == null || rawTargetOrNull() == null) return this

    var num = parentOrNull()!!.toIntOrNull() ?: error("Cannot normalize invalid numeric link: $this")
    @OptIn(InternalTopologyApi::class) var den = rawTargetOrNull()!!.toIntOrNull() ?: error("Cannot normalize invalid numeric link: $this")
    require(den != 0) { "Denominator is zero" }

    // Sign normalization: ensure positive denominator
    if (den < 0) { num = -num; den = -den }

    // GCD reduction
    if (num == 0) return N0
    val g = gcd(abs(num), den)
    num /= g; den /= g

    return if (den == 1) integerLink(num)                   // Integer collapse: denominator-1 rationals are integers
    else integerLink(num).then(integerLink(den) as Link?)   // Construct canonical rational Link(N_p, N_q)
}

/** Euclidean GCD for non-negative integers. */
private fun gcd(a: Int, b: Int): Int = if (b == 0) a else gcd(b, a % b)

// ============================================================
//  Quantity Type-Safe Wrapper (JIT Projection Layer)
// ============================================================

/**
 * Exact quantity representing a number.
 * Caches the parsed numerator and denominator, and holds the canonical Link representation.
 */
class Quantity internal constructor(val numerator: Int, val denominator: Int, override val link: Link) : Linkable, Comparable<Quantity> {

    init { require(denominator != 0) { "Denominator must not be zero." } }

    operator fun plus(other: Quantity): Quantity = (link add other.link).normalize().toQuantityOrNull()!!
    operator fun plus(other: Int): Quantity = (link add integerLink(other)).normalize().toQuantityOrNull()!!

    operator fun minus(other: Quantity): Quantity = (link add (integerLink(-1) mul other.link)).normalize().toQuantityOrNull()!!
    operator fun minus(other: Int): Quantity = (link add integerLink(-other)).normalize().toQuantityOrNull()!!

    operator fun times(other: Quantity): Quantity = (link mul other.link).normalize().toQuantityOrNull()!!
    operator fun times(other: Int): Quantity = (link mul integerLink(other)).normalize().toQuantityOrNull()!!

    operator fun div(other: Quantity): Quantity = (link div other.link).normalize().toQuantityOrNull()!!
    operator fun div(other: Int): Quantity = (link div integerLink(other)).normalize().toQuantityOrNull()!!

    operator fun unaryMinus(): Quantity = (integerLink(-1) mul link).normalize().toQuantityOrNull()!!

    fun abs(): Quantity = if (compareTo(ZERO) >= 0) this else -this
    fun isPositive(): Boolean = numerator > 0
    fun isNegative(): Boolean = numerator < 0

    fun reciprocal(): Quantity = (integerLink(1) div link).normalize().toQuantityOrNull()!!

    fun toLong(): Long = numerator.toLong() / denominator.toLong()
    fun toInt(): Int = numerator / denominator
    fun toDouble(): Double = numerator.toDouble() / denominator.toDouble()

    // Compare on the cached normalized rationals: denominators are positive (sign lives in the numerator), so
    // cross-multiplication preserves order. Done in Long to avoid Int overflow on the cross product.
    override fun compareTo(other: Quantity): Int =
        (numerator.toLong() * other.denominator).compareTo(other.numerator.toLong() * denominator)

    override fun hashCode(): Int = link.hashCode()
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is Quantity) return false
        return link == other.link
    }

    override fun toString(): String = if (denominator == 1) "$numerator" else "$numerator/$denominator"

    companion object {
        val ZERO: Quantity = Quantity(0, 1, N0)
        val ONE: Quantity = Quantity(1, 1, integerLink(1))

        operator fun invoke(numerator: Int, denominator: Int = 1): Quantity {
            require(denominator != 0) { "Denominator must not be zero." }
            val link = if (denominator == 1) integerLink(numerator)
                       else (integerLink(numerator) div integerLink(denominator)).normalize()
            return link.toQuantityOrNull() ?: error("Invalid quantity")
        }
    }
}

operator fun Int.plus(other: Quantity): Quantity = other + this
operator fun Int.minus(other: Quantity): Quantity = Quantity(this) - other
operator fun Int.times(other: Quantity): Quantity = other * this
operator fun Int.div(other: Quantity): Quantity = Quantity(this) / other

/** Attach a quantity as the raw target of a relation without leaking the quantity grammar to commitment callers. */
fun NestedLink.then(quantity: Quantity): NestedLink = then(quantity.link)

/** Render a relation's raw target as a quantity in the local quantity grammar. */
@OptIn(InternalTopologyApi::class)
fun NestedLink.targetAsQuantity(): Quantity? = rawTargetOrNull()?.toQuantityOrNull()

/** Retrieve the target of this link as an integer, or null if it's not a valid 1D integer. */
@OptIn(InternalTopologyApi::class)
fun NestedLink.targetAsInt(): Int? = rawTargetOrNull()?.toIntOrNull()

